import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom, from, switchMap, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import type { LoginRequestBody, LoginResponse, RefreshTokenRequestBody } from '../api';
import { TokenStore } from '../auth/token-store';
import type { AppLocale } from '../i18n/locale.model';
import { translateStatic } from '../i18n/translate-static';
import { LS_USER_DISPLAY_NAME, LS_USER_EMAIL } from '../storage/session-keys';
import { MasterCompanyService } from './master-company.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly masterCompany = inject(MasterCompanyService);
  private readonly tokens = inject(TokenStore);

  /** Evita múltiplos POST /user/refresh-token em paralelo após vários 401. */
  private tokenRefreshInFlight: { gen: number; promise: Promise<void> } | null = null;

  login(userName: string, password: string): Observable<LoginResponse> {
    const trimmedUser = userName.trim();

    return from(this.masterCompany.getMasterCompanyId()).pipe(
      switchMap((companyId) => {
        const body: LoginRequestBody = { companyId, userName: trimmedUser, password };
        return this.http.post<LoginResponse>(`${environment.apiBaseUrl}/user/login`, body);
      }),
      tap((res) => {
        if (res.token) {
          this.tokens.setAccessToken(res.token);
        }
        if (res.refreshToken) {
          this.tokens.setRefreshToken(res.refreshToken);
        }
        const displayName = res.userName?.trim() || trimmedUser || '';
        localStorage.setItem(LS_USER_DISPLAY_NAME, displayName);
        localStorage.setItem(LS_USER_EMAIL, res.email?.trim() ?? '');
      }),
    );
  }

  clearTokens(): void {
    this.tokens.clear();
  }

  /** Encerra sessão no cliente (tokens + dados exibidos no header). */
  logout(): void {
    this.clearTokens();
    localStorage.removeItem(LS_USER_DISPLAY_NAME);
    localStorage.removeItem(LS_USER_EMAIL);
  }

  getStoredUserDisplayName(): string {
    return localStorage.getItem(LS_USER_DISPLAY_NAME) ?? '';
  }

  getStoredUserEmail(): string {
    return localStorage.getItem(LS_USER_EMAIL) ?? '';
  }

  /** Atualiza nome/e-mail exibidos no header (ex.: após GET /user). */
  setStoredUserProfile(displayName: string, email: string): void {
    localStorage.setItem(LS_USER_DISPLAY_NAME, displayName);
    localStorage.setItem(LS_USER_EMAIL, email);
  }

  getAccessToken(): string | null {
    return this.tokens.getAccessToken();
  }

  getRefreshToken(): string | null {
    return this.tokens.getRefreshToken();
  }

  /**
   * Troca o refresh token por novos tokens. Requisições concorrentes aguardam a mesma chamada.
   */
  performTokenRefresh(): Promise<void> {
    const gen = this.tokens.generation;
    if (this.tokenRefreshInFlight?.gen === gen) {
      return this.tokenRefreshInFlight.promise;
    }
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearTokens();
      return Promise.reject(new Error('Sem refresh token'));
    }
    const url = `${environment.apiBaseUrl}/user/refresh-token`;
    const body: RefreshTokenRequestBody = { refreshToken };
    const promise = firstValueFrom(this.http.post<LoginResponse>(url, body))
      .then((res) => {
        if (this.tokens.generation !== gen) {
          throw new Error('Sessão encerrada durante o refresh');
        }
        if (res.token) {
          this.tokens.setAccessToken(res.token);
        }
        if (res.refreshToken) {
          this.tokens.setRefreshToken(res.refreshToken);
        }
        if (!res.token) {
          throw new Error('Resposta de refresh sem access token');
        }
      })
      .catch((error: unknown) => {
        if (this.tokens.generation === gen) {
          this.clearTokens();
        }
        throw error;
      })
      .finally(() => {
        if (this.tokenRefreshInFlight?.gen === gen) {
          this.tokenRefreshInFlight = null;
        }
      });
    this.tokenRefreshInFlight = { gen, promise };
    return promise;
  }

  /** 401 com corpo típico da API quando o access token expirou. */
  static isExpiredAccessTokenError(err: HttpErrorResponse): boolean {
    if (err.status !== 401) {
      return false;
    }
    const body = err.error;
    if (typeof body !== 'object' || body === null) {
      return false;
    }
    const msg = (body as { message?: unknown }).message;
    if (typeof msg === 'string') {
      return msg.toLowerCase().includes('token expired');
    }
    if (Array.isArray(msg)) {
      return msg.some((m) => typeof m === 'string' && m.toLowerCase().includes('token expired'));
    }
    return false;
  }

  static getErrorMessage(err: unknown, locale?: AppLocale): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (typeof body === 'object' && body !== null && 'message' in body) {
        const msg = (body as { message?: unknown }).message;
        if (typeof msg === 'string') return msg;
        if (Array.isArray(msg)) return msg.filter((m) => typeof m === 'string').join(', ');
      }
      if (err.status === 0) {
        return translateStatic('common.errors.apiConnection', undefined, locale);
      }
      return (
        err.message || translateStatic('common.errors.httpStatus', { status: err.status }, locale)
      );
    }
    if (err instanceof Error) {
      return err.message;
    }
    return translateStatic('common.errors.loginFailed', undefined, locale);
  }
}
